import { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  MenuItem,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  AggregatorOption,
  BankRow,
  BankFormData,
  SettlementFormValue,
  SETTLEMENT_DATE_OPTIONS,
  DEFAULT_SETTLEMENT_DATE,
  coerceOptionalSettlementDate,
  getGatewayParam,
} from '../types/bankdetails';

interface BankFormDialogProps {
  open: boolean;
  onClose: () => void;
  title: string;
  onSubmit: (formData: BankFormData) => void;
  aggregator: AggregatorOption;
  merchantId?: string;
  mode: 'add' | 'edit';
  editingIndex?: number | null;
  rows?: BankRow[];
  onOpenHistory?: (bank: BankRow) => void;
}

export function BankFormDialog({
  open,
  onClose,
  title,
  onSubmit,
  aggregator,
  merchantId,
  mode,
  editingIndex,
  rows,
  onOpenHistory,
}: BankFormDialogProps) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const showSettlement = Boolean(merchantId);

  const [formData, setFormData] = useState<BankFormData>({
    gateway: aggregator,
    name: '',
    ifscCode: '',
    nbBankId: '',
    nbHybridBankId: '',
    nbDebitCardBankId: '',
    nbBankIdCorporate: '',
    nbHybridBankIdCorporate: '',
    nbDebitCardBankIdCorporate: '',
    nbLumpsum: true,
    nbHybrid: false,
    nbDebitCard: false,
    emdBankId: '',
    emdBankIdCorporate: '',
    emdNetBanking: true,
    emdDebitCard: true,
    emdAadhaar: true,
    emdPan: true,
    nbLumpsumSettlement: DEFAULT_SETTLEMENT_DATE,
    nbHybridSettlement: DEFAULT_SETTLEMENT_DATE,
    nbDebitCardSettlement: DEFAULT_SETTLEMENT_DATE,
    emdNetBankingSettlement: DEFAULT_SETTLEMENT_DATE,
    emdDebitCardSettlement: DEFAULT_SETTLEMENT_DATE,
    emdAadhaarSettlement: DEFAULT_SETTLEMENT_DATE,
    emdPanSettlement: DEFAULT_SETTLEMENT_DATE,
    remarks: '',
  });

  // Reset form when dialog opens/closes
  useEffect(() => {
    const hasValidIndex = typeof editingIndex === 'number' && editingIndex >= 0;
    if (open && mode === 'edit' && hasValidIndex && rows && rows[editingIndex]) {
      const row = rows[editingIndex]!;
      setFormData({
        gateway: getGatewayParam(aggregator),
        name: row.name,
        ifscCode: row.ifscCode,
        nbBankId: row.netbanking?.bankId || '-',
        nbHybridBankId: row.netbanking?.hybridBankId || '-',
        nbDebitCardBankId: row.netbanking?.lumpsumDebitCardBankId || '-',
        nbBankIdCorporate: row.netbanking?.bankIdCorporate || '',
        nbHybridBankIdCorporate: row.netbanking?.hybridBankIdCorporate || '',
        nbDebitCardBankIdCorporate: row.netbanking?.lumpsumDebitCardBankIdCorporate || '',
        nbLumpsum: !!row.netbanking?.lumpsum,
        nbHybrid: !!row.netbanking?.hybrid,
        nbDebitCard: !!row.netbanking?.lumpsumDebitCard,
        emdBankId: row.emandate?.bankId || '-',
        emdBankIdCorporate: row.emandate?.bankIdCorporate || '',
        emdNetBanking: !!row.emandate?.netBanking,
        emdDebitCard: !!row.emandate?.debitCard,
        emdAadhaar: !!row.emandate?.aadhaar,
        emdPan: !!row.emandate?.pan,
        nbLumpsumSettlement: coerceOptionalSettlementDate(row.netbanking?.lumpsumSettlement) ?? '',
        nbHybridSettlement: coerceOptionalSettlementDate(row.netbanking?.hybridSettlement) ?? '',
        nbDebitCardSettlement: coerceOptionalSettlementDate(row.netbanking?.lumpsumDebitCardSettlement) ?? '',
        emdNetBankingSettlement: coerceOptionalSettlementDate(row.emandate?.netBankingSettlement) ?? '',
        emdDebitCardSettlement: coerceOptionalSettlementDate(row.emandate?.debitCardSettlement) ?? '',
        emdAadhaarSettlement: coerceOptionalSettlementDate(row.emandate?.aadhaarSettlement) ?? '',
        emdPanSettlement: coerceOptionalSettlementDate(row.emandate?.panSettlement) ?? '',
        remarks: '',
      });
    } else if (open && mode === 'add') {
      setFormData({
        gateway: aggregator,
        name: '',
        ifscCode: '',
        nbBankId: '',
        nbHybridBankId: '',
        nbDebitCardBankId: '',
        nbBankIdCorporate: '',
        nbHybridBankIdCorporate: '',
        nbDebitCardBankIdCorporate: '',
        nbLumpsum: true,
        nbHybrid: false,
        nbDebitCard: false,
        emdBankId: '',
        emdBankIdCorporate: '',
        emdNetBanking: true,
        emdDebitCard: true,
        emdAadhaar: true,
        emdPan: true,
        nbLumpsumSettlement: DEFAULT_SETTLEMENT_DATE,
        nbHybridSettlement: DEFAULT_SETTLEMENT_DATE,
        nbDebitCardSettlement: DEFAULT_SETTLEMENT_DATE,
        emdNetBankingSettlement: DEFAULT_SETTLEMENT_DATE,
        emdDebitCardSettlement: DEFAULT_SETTLEMENT_DATE,
        emdAadhaarSettlement: DEFAULT_SETTLEMENT_DATE,
        emdPanSettlement: DEFAULT_SETTLEMENT_DATE,
        remarks: '',
      });
    }
  }, [open, mode, editingIndex, rows, aggregator]);

  const handleSubmit = async () => {
    if (!formData.name || !formData.ifscCode) return;
    onSubmit(formData);
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle sx={{ pr: 2 }}>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          spacing={2}
        >
          <span>{title}</span>
          <Chip
            label={aggregator}
            size="small"
            color="primary"
            variant="outlined"
            sx={{ fontWeight: 500 }}
          />
        </Stack>
      </DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
          {!showSettlement && (
            <Alert severity="info" sx={{ borderRadius: 2 }}>
              Settlement day fields are hidden. Enter a Merchant ID on the Bank Details page to manage merchant-specific settlement days.
            </Alert>
          )}
          <Box sx={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: 2 }}>
            <TextField
              label="Bank Name"
              value={formData.name}
              onChange={(e) => setFormData((p) => ({ ...p, name: e.target.value }))}
              fullWidth
            />
            <TextField
              label="Bank IFSC"
              value={formData.ifscCode}
              onChange={(e) => setFormData((p) => ({ ...p, ifscCode: e.target.value }))}
              fullWidth
            />
          </Box>

          <TableContainer
            component={Paper}
            sx={{ borderRadius: '12px', border: `1px solid ${theme.palette.divider}` }}
          >
            <Table
              size="small"
              sx={{
                '& th, & td': { border: `1px solid ${theme.palette.divider}` },
                '& thead th': {
                  backgroundColor: theme.palette.action.hover,
                  fontWeight: 600,
                  textAlign: 'center',
                },
                '& tbody td': { verticalAlign: 'top', p: 1.5 },
              }}
            >
              <TableHead>
                <TableRow>
                  <TableCell align="center" colSpan={3} sx={{ fontWeight: 600 }}>
                    Netbanking
                  </TableCell>
                  <TableCell align="center" sx={{ fontWeight: 600 }}>
                    Emandate / OTM
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell align="center">Lumpsum</TableCell>
                  <TableCell align="center">Hybrid / iSIP</TableCell>
                  <TableCell align="center">Debit Card</TableCell>
                  <TableCell align="center">Emandate</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                <TableRow>
                  <TableCell sx={{ minWidth: 160 }}>
                    <Stack spacing={1.25}>
                      <TextField
                        size="small"
                        fullWidth
                        label="Bank ID"
                        value={formData.nbBankId}
                        placeholder="-"
                        inputProps={{ style: { textAlign: 'center' } }}
                        onChange={(e) => setFormData((p) => ({ ...p, nbBankId: e.target.value }))}
                      />
                      <TextField
                        size="small"
                        fullWidth
                        label="Corp Bank ID"
                        value={formData.nbBankIdCorporate}
                        placeholder="-"
                        inputProps={{ style: { textAlign: 'center', fontFamily: 'monospace' } }}
                        onChange={(e) => setFormData((p) => ({ ...p, nbBankIdCorporate: e.target.value }))}
                      />
                      <FormControlLabel
                        sx={{ m: 0, justifyContent: 'center' }}
                        control={
                          <Checkbox
                            checked={formData.nbLumpsum}
                            onChange={(e) =>
                              setFormData((p) => ({ ...p, nbLumpsum: e.target.checked }))
                            }
                          />
                        }
                        label={<Typography variant="caption">Enabled</Typography>}
                      />
                      {showSettlement && (
                        <TextField
                          size="small"
                          select
                          fullWidth
                          label="Settlement"
                          value={formData.nbLumpsumSettlement}
                          onChange={(e) =>
                            setFormData((p) => ({
                              ...p,
                              nbLumpsumSettlement: e.target.value as SettlementFormValue,
                            }))
                          }
                        >
                          <MenuItem value=""><em>None</em></MenuItem>
                          {SETTLEMENT_DATE_OPTIONS.map((opt) => (
                            <MenuItem key={opt} value={opt}>
                              {opt}
                            </MenuItem>
                          ))}
                        </TextField>
                      )}
                    </Stack>
                  </TableCell>
                  <TableCell sx={{ minWidth: 160 }}>
                    <Stack spacing={1.25}>
                      <TextField
                        size="small"
                        fullWidth
                        label="Bank ID"
                        value={formData.nbHybridBankId}
                        placeholder="-"
                        inputProps={{ style: { textAlign: 'center' } }}
                        onChange={(e) =>
                          setFormData((p) => ({ ...p, nbHybridBankId: e.target.value }))
                        }
                      />
                      <TextField
                        size="small"
                        fullWidth
                        label="Corp Bank ID"
                        value={formData.nbHybridBankIdCorporate}
                        placeholder="-"
                        inputProps={{ style: { textAlign: 'center', fontFamily: 'monospace' } }}
                        onChange={(e) =>
                          setFormData((p) => ({ ...p, nbHybridBankIdCorporate: e.target.value }))
                        }
                      />
                      <FormControlLabel
                        sx={{ m: 0, justifyContent: 'center' }}
                        control={
                          <Checkbox
                            checked={formData.nbHybrid}
                            onChange={(e) =>
                              setFormData((p) => ({ ...p, nbHybrid: e.target.checked }))
                            }
                          />
                        }
                        label={<Typography variant="caption">Enabled</Typography>}
                      />
                      {showSettlement && (
                        <TextField
                          size="small"
                          select
                          fullWidth
                          label="Settlement"
                          value={formData.nbHybridSettlement}
                          onChange={(e) =>
                            setFormData((p) => ({
                              ...p,
                              nbHybridSettlement: e.target.value as SettlementFormValue,
                            }))
                          }
                        >
                          <MenuItem value=""><em>None</em></MenuItem>
                          {SETTLEMENT_DATE_OPTIONS.map((opt) => (
                            <MenuItem key={opt} value={opt}>
                              {opt}
                            </MenuItem>
                          ))}
                        </TextField>
                      )}
                    </Stack>
                  </TableCell>
                  <TableCell sx={{ minWidth: 160 }}>
                    <Stack spacing={1.25}>
                      <TextField
                        size="small"
                        fullWidth
                        label="Bank ID"
                        value={formData.nbDebitCardBankId}
                        placeholder="-"
                        inputProps={{ style: { textAlign: 'center' } }}
                        onChange={(e) =>
                          setFormData((p) => ({ ...p, nbDebitCardBankId: e.target.value }))
                        }
                      />
                      <TextField
                        size="small"
                        fullWidth
                        label="Corp Bank ID"
                        value={formData.nbDebitCardBankIdCorporate}
                        placeholder="-"
                        inputProps={{ style: { textAlign: 'center', fontFamily: 'monospace' } }}
                        onChange={(e) =>
                          setFormData((p) => ({ ...p, nbDebitCardBankIdCorporate: e.target.value }))
                        }
                      />
                      <FormControlLabel
                        sx={{ m: 0, justifyContent: 'center' }}
                        control={
                          <Checkbox
                            checked={formData.nbDebitCard}
                            onChange={(e) =>
                              setFormData((p) => ({ ...p, nbDebitCard: e.target.checked }))
                            }
                          />
                        }
                        label={<Typography variant="caption">Enabled</Typography>}
                      />
                      {showSettlement && (
                        <TextField
                          size="small"
                          select
                          fullWidth
                          label="Settlement"
                          value={formData.nbDebitCardSettlement}
                          onChange={(e) =>
                            setFormData((p) => ({
                              ...p,
                              nbDebitCardSettlement: e.target.value as SettlementFormValue,
                            }))
                          }
                        >
                          <MenuItem value=""><em>None</em></MenuItem>
                          {SETTLEMENT_DATE_OPTIONS.map((opt) => (
                            <MenuItem key={opt} value={opt}>
                              {opt}
                            </MenuItem>
                          ))}
                        </TextField>
                      )}
                    </Stack>
                  </TableCell>
                  <TableCell sx={{ minWidth: 200 }}>
                    <Stack spacing={1.25}>
                      <TextField
                        size="small"
                        fullWidth
                        label="Bank ID"
                        value={formData.emdBankId}
                        placeholder="-"
                        inputProps={{ style: { textAlign: 'center' } }}
                        onChange={(e) =>
                          setFormData((p) => ({ ...p, emdBankId: e.target.value }))
                        }
                      />
                      <TextField
                        size="small"
                        fullWidth
                        label="Corp Bank ID"
                        value={formData.emdBankIdCorporate}
                        placeholder="-"
                        inputProps={{ style: { textAlign: 'center', fontFamily: 'monospace' } }}
                        onChange={(e) =>
                          setFormData((p) => ({ ...p, emdBankIdCorporate: e.target.value }))
                        }
                      />
                      <Box
                        sx={{
                          display: 'grid',
                          gridTemplateColumns: '1fr 1fr',
                          rowGap: 0,
                          columnGap: 1,
                        }}
                      >
                        <FormControlLabel
                          sx={{ m: 0 }}
                          control={
                            <Checkbox
                              size="small"
                              checked={formData.emdNetBanking}
                              onChange={(e) =>
                                setFormData((p) => ({ ...p, emdNetBanking: e.target.checked }))
                              }
                            />
                          }
                          label={<Typography variant="caption">Net Banking</Typography>}
                        />
                        <FormControlLabel
                          sx={{ m: 0 }}
                          control={
                            <Checkbox
                              size="small"
                              checked={formData.emdDebitCard}
                              onChange={(e) =>
                                setFormData((p) => ({ ...p, emdDebitCard: e.target.checked }))
                              }
                            />
                          }
                          label={<Typography variant="caption">Debit Card</Typography>}
                        />
                        <FormControlLabel
                          sx={{ m: 0 }}
                          control={
                            <Checkbox
                              size="small"
                              checked={formData.emdAadhaar}
                              onChange={(e) =>
                                setFormData((p) => ({ ...p, emdAadhaar: e.target.checked }))
                              }
                            />
                          }
                          label={<Typography variant="caption">Aadhaar</Typography>}
                        />
                        <FormControlLabel
                          sx={{ m: 0 }}
                          control={
                            <Checkbox
                              size="small"
                              checked={formData.emdPan}
                              onChange={(e) =>
                                setFormData((p) => ({ ...p, emdPan: e.target.checked }))
                              }
                            />
                          }
                          label={<Typography variant="caption">PAN</Typography>}
                        />
                      </Box>
                      {showSettlement && (
                        <Box
                          sx={{
                            display: 'grid',
                            gridTemplateColumns: '1fr 1fr',
                            gap: 1,
                          }}
                        >
                          {([
                            { key: 'emdNetBankingSettlement', label: 'NB Settlement' },
                            { key: 'emdDebitCardSettlement', label: 'DC Settlement' },
                            { key: 'emdAadhaarSettlement', label: 'AD Settlement' },
                            { key: 'emdPanSettlement', label: 'PAN Settlement' },
                          ] as const).map(({ key, label }) => (
                            <TextField
                              key={key}
                              size="small"
                              select
                              fullWidth
                              label={label}
                              value={formData[key]}
                              onChange={(e) =>
                                setFormData((p) => ({
                                  ...p,
                                  [key]: e.target.value as SettlementFormValue,
                                }))
                              }
                            >
                              <MenuItem value=""><em>None</em></MenuItem>
                              {SETTLEMENT_DATE_OPTIONS.map((opt) => (
                                <MenuItem key={opt} value={opt}>
                                  {opt}
                                </MenuItem>
                              ))}
                            </TextField>
                          ))}
                        </Box>
                      )}
                    </Stack>
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>

          <TextField
            label="Remarks"
            placeholder="Optional notes"
            fullWidth
            size="small"
            multiline
            minRows={1}
            maxRows={4}
            value={formData.remarks}
            onChange={(e) => setFormData((p) => ({ ...p, remarks: e.target.value }))}
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="inherit">
          Cancel
        </Button>
        {mode === 'edit' &&
          onOpenHistory &&
          typeof editingIndex === 'number' &&
          rows &&
          rows[editingIndex] && (
            <Button
              onClick={() => onOpenHistory(rows[editingIndex]!)}
              variant="outlined"
              color="info"
            >
              View History
            </Button>
          )}
        <Button onClick={handleSubmit} variant="contained">
          {mode === 'add' ? 'Add Bank' : 'Update Bank'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
